import * as Sentry from '@sentry/react';
import { resolveEnvironment } from '../utils/environment';

type InitSentryReactOptions = {
  appName: string;
  dsn?: string | null;
  environment?: string | null;
  release?: string | null;
  tracesSampleRate?: number;
};

type SentryUserContext = {
  userId: string;
  tenantId?: string | null;
};

const initializedApps = new Set<string>();

function scrubHeaders(headers: unknown): unknown {
  if (!headers || typeof headers !== 'object') return headers;
  return Object.fromEntries(
    Object.entries(headers as Record<string, unknown>).map(([key, value]) => {
      const lower = key.toLowerCase();
      if (lower.includes('authorization') || lower.includes('cookie') || lower.includes('token')) {
        return [key, '[REDACTED]'];
      }
      return [key, value];
    }),
  );
}

export function initSentryReact(options: InitSentryReactOptions): boolean {
  const dsn = options.dsn?.trim();
  if (!dsn) return false;
  if (initializedApps.has(options.appName)) return true;

  const environment = resolveEnvironment(options.environment);
  Sentry.init({
    dsn,
    environment,
    release: options.release?.trim() || undefined,
    tracesSampleRate: options.tracesSampleRate ?? (environment === 'production' ? 0.1 : 1),
    beforeSend(event) {
      const next = { ...event };
      if (next.request) {
        const request = next.request as { headers?: unknown } | undefined;
        next.request = {
          ...next.request,
          headers: scrubHeaders(request?.headers) as Record<string, string> | undefined,
        };
      }
      next.tags = { ...(next.tags ?? {}), app: options.appName };
      return next;
    },
  });

  initializedApps.add(options.appName);
  Sentry.setTag('app', options.appName);
  return true;
}

export function captureSentryException(error: unknown, context?: Record<string, unknown>): void {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export function setSentryUserContext(context: SentryUserContext | null): void {
  if (!context) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({ id: context.userId });
  if (context.tenantId) {
    Sentry.setTag('tenant_id', context.tenantId);
  }
}

export function addSentryBreadcrumb(input: {
  category: string;
  message: string;
  level?: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
  data?: Record<string, unknown>;
}): void {
  Sentry.addBreadcrumb({
    category: input.category,
    message: input.message,
    level: input.level ?? 'info',
    data: input.data,
  });
}
